import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { BookDown, Loader2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { usePaperCitationsApi } from "../../api/citations";
import { useCitations } from "../../context/CitationProvider";
import {
  deduplicateCitations,
  downloadCitations,
  type ExportFormat,
} from "../../utils/citationExport";
import { enrichCitations } from "../../utils/citationFormat";
import { ActionButton } from "./ActionButton";

const FORMAT_OPTIONS: { value: ExportFormat; label: string }[] = [
  { value: "ris", label: "RIS (Zotero, Mendeley, EndNote)" },
  { value: "bibtex", label: "BibTeX (LaTeX)" },
  { value: "txt", label: "Plain text" },
];

export const DownloadRefsButton = () => {
  const [downloading, setDownloading] = useState(false);
  const { citations } = useCitations();
  const { fetchCitationMetadata } = usePaperCitationsApi();

  const exportable = useMemo(
    () => deduplicateCitations(citations),
    [citations],
  );

  const handleDownload = useCallback(
    async (format: ExportFormat) => {
      setDownloading(true);
      try {
        const enriched = await enrichCitations(
          citations,
          fetchCitationMetadata,
        );
        downloadCitations(enriched, format);
      } catch (err) {
        console.error("Failed to download references:", err);
      } finally {
        setDownloading(false);
      }
    },
    [citations, fetchCitationMetadata],
  );

  if (exportable.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div>
          <ActionButton
            label="Download references"
            onClick={() => {}}
            disabled={downloading}
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <BookDown className="h-3.5 w-3.5" />
            )}
          </ActionButton>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="min-w-[220px]">
        {FORMAT_OPTIONS.map(({ value, label }) => (
          <DropdownMenuItem key={value} onSelect={() => handleDownload(value)}>
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
